<script setup lang="ts">
import { AssociatedAccount, LinkedAccountData, Rights, Trainer } from "../../types/PersologOnlineTypes.ts";
import { onMounted, ref, watch, PropType, computed } from "vue";
import { ProfileAdministrationAPI } from "../../api";
import { useI18n } from "vue-i18n";
import TableSkeleton from "./TableSkeleton.vue";
import AccountLinkTable from "./AccountLinkTable.vue";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { isMobileView, isValidEmail } from "../../utils/GeneralUtils.ts";

const { t } = useI18n();

const props = defineProps({
    visibleToggle: {
        type: Boolean,
        default: false,
    },
    trainer: {
        type: Object as PropType<Trainer>,
        required: true,
    },
});

const emit = defineEmits(["update:visible", "update:impersonated"]);

const loading = ref(false);
const sending = ref(false);
const visible = ref(props.visibleToggle);
const selectedRights = ref<{ name: string; value: Rights }>({
    name: t(`header.accountSettings.accountLinks.${Rights.PROFILES.toLowerCase()}`),
    value: Rights.PROFILES,
});
const accountsGrantedAccessByTrainer = ref<AssociatedAccount[]>([]);
const accountsAccessibleByTrainer = ref<AssociatedAccount[]>([]);

const toast = useToast();
const confirm = useConfirm();

const showSuccessLinking = () => {
    toast.add({
        severity: "success",
        summary: t("header.accountSettings.accountLinks.validation.heading"),
        detail: t("header.accountSettings.accountLinks.validation.success"),
        life: 6000,
    });
};

const showErrorLinking = () => {
    toast.add({
        severity: "error",
        summary: t("header.accountSettings.accountLinks.validation.heading"),
        detail: t("header.accountSettings.accountLinks.validation.error"),
        life: 8000,
    });
};

const getConfirmationMessage = (email: string) => {
    if (selectedRights.value.value === Rights.PROFILES) {
        return t("header.accountSettings.accountLinks.confirmation.messageTransferProfiles", { email: email });
    } else if (selectedRights.value.value === Rights.PROJECTS) {
        return t("header.accountSettings.accountLinks.confirmation.messageManageProjects", { email: email });
    }
};

const accountMail = ref("");
const isValid = ref(true);
const emailErrorMessage = ref("");

const rightOptions = ref(
    Object.keys(Rights).map((key) => {
        return {
            name: computed(() => t(`header.accountSettings.accountLinks.${key.toLowerCase()}`)),
            value: Rights[key as keyof typeof Rights],
        };
    }),
);

const columns = [
    { field: "account", text: "header.accountSettings.accountLinks.account" },
    { field: "rights", text: "header.accountSettings.accountLinks.rights" },
    { field: "actions", text: "header.accountSettings.accountLinks.actions" },
];

watch(
    () => visible.value,
    () => {
        emit("update:visible", visible.value);
    },
);

watch(
    () => props.visibleToggle,
    () => {
        visible.value = props.visibleToggle;
    },
);

/**
 * Check if email is valid and make it lowercase
 */
watch(
    () => accountMail.value,
    () => {
        accountMail.value = accountMail.value.toLowerCase();
        isValid.value = isValidEmail(accountMail.value);
    },
);

/**
 * Link an account to the trainer
 */
async function linkAccount() {
    if (isValidEmail(accountMail.value) && await isLinkable(accountMail.value)) {
        sending.value = true;
        const data: LinkedAccountData = {
            trainerEmail: accountMail.value,
            associatedAccountId: props.trainer.id,
            rights: selectedRights.value.value,
        };
        try {
            const newLink = await ProfileAdministrationAPI.linkAccount(data);
            if (newLink) {
                accountsGrantedAccessByTrainer.value.push(newLink);
                showSuccessLinking();
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            if (error.response && error.response.status === 404) {
                showErrorLinking();
            } else {
                console.error("An unexpected error occurred:", error);
            }
        } finally {
            sending.value = false;
        }
    } else {
        console.error("Invalid email");
    }
}

/**
 * Confirm the linking of an account
 */
function confirmLinking() {
    confirm.require({
        group: "confirm-linking",
        message: getConfirmationMessage(accountMail.value),
        header: t("header.accountSettings.accountLinks.confirmation.heading"),
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: t("buttons.cancel"),
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            id: "link-account-view_btn_confirm",
            label: t("buttons.ok"),
        },
        accept: () => {
            linkAccount();
        },
    });
}

function validateGrantedAccessAccounts(email: string, rights: Rights) {
    return accountsGrantedAccessByTrainer.value.some(
        (account) => account.trainer.email === email && account.rights === rights,
    );
}

async function validate(email: string) {
    if (
        email === "" ||
        (isValidEmail(email) &&
            await isLinkable(accountMail.value) &&
            email !== props.trainer.email &&
            !validateGrantedAccessAccounts(email, selectedRights.value.value))
    ) {
        emailErrorMessage.value = "";
        isValid.value = true;
    } else {
        if (!isValidEmail(email)) {
            emailErrorMessage.value = t("header.accountSettings.accountLinks.validation.email");
        } else if (email === props.trainer.email) {
            emailErrorMessage.value = t("header.accountSettings.accountLinks.validation.self");
        } else if (validateGrantedAccessAccounts(email, selectedRights.value.value)) {
            emailErrorMessage.value = t("header.accountSettings.accountLinks.validation.alreadyGranted");
        } else {
            emailErrorMessage.value = t("header.accountSettings.accountLinks.validation.distributor");
        }
        isValid.value = false;
    }
}

function updateLinks(updatedLinks: AssociatedAccount[]) {
    accountsGrantedAccessByTrainer.value = updatedLinks;
}

async function isLinkable(email: string): Promise<boolean> {
    const encodedEmail = btoa(email);
    return await ProfileAdministrationAPI.isLinkable(props.trainer?.id, encodedEmail)
}

onMounted(async () => {
    loading.value = true;
    const [grantedAccess, accessibleByTrainer] = await Promise.all([
        ProfileAdministrationAPI.getAccountsGrantedAccessByTrainer(props.trainer?.id),
        ProfileAdministrationAPI.getAccountsAccessibleByTrainer(props.trainer?.id),
    ]);

    accountsGrantedAccessByTrainer.value = grantedAccess;
    accountsAccessibleByTrainer.value = accessibleByTrainer;
    loading.value = false;
});
</script>

<template>
    <Toast />
    <Dialog
        v-model:visible="visible"
        modal
        header="Header"
        :style="{ minWidth: '80vw', maxWidth: '90vw' }"
        :breakpoints="{ '1199px': '85vw', '575px': '80vw' }"
        :dismissable-mask="false"
        :block-scroll="true"
        :draggable="false"
    >
        <template #header>
            <div class="inline-flex items-center justify-center gap-2">
                <span class="font-bold whitespace-nowrap">
                    {{ t("header.accountSettings.accountLinks.heading") }} - {{ trainer?.firstname }}
                    {{ trainer?.lastname }}
                </span>
            </div>
        </template>
        <div class="flex flex-wrap gap-4 mt-4">
            <div class="flex-1">
                <Panel :header="t('profile-overview.instructions.heading')" toggleable :collapsed="isMobileView()">
                    {{ t("header.accountSettings.accountLinks.instructions") }}
                </Panel>
            </div>
        </div>
        <Divider align="left">
            <b>{{ t("header.accountSettings.accountLinks.add") }}</b>
        </Divider>
        <div class="flex flex-wrap gap-4">
            <div v-if="loading" class="flex-1 flex gap-2">
                <Skeleton width="12rem" height="3rem"></Skeleton>
                <Skeleton width="12rem" height="3rem"></Skeleton>
                <Skeleton size="3rem"></Skeleton>
            </div>
            <div v-else class="flex-1 md:flex gap-2">
                <InputText
                    aria-describedby="email-link-account-view_small_email-help"
                    v-model="accountMail"
                    type="text"
                    class="w-full md:w-56 mb-1 md:mb-0"
                    id="link-account-view_input_accountMailInput"
                    placeholder="info@example.com"
                    @blur="() => validate(accountMail)"
                    :invalid="!isValid"
                />
                <Select
                    id="link-account-view_drop-down_rights"
                    v-model="selectedRights"
                    :options="rightOptions"
                    option-label="name"
                    class="w-full md:w-56 mb-1 md:mb-0"
                />
                <Button v-if="sending" icon="pi pi-spin pi-spinner" :aria-label="t('accessibility.buttons.sending')" />
                <Button
                    v-else
                    id="link-account-view_btn_add"
                    v-tooltip="t('header.accountSettings.accountLinks.tooltips.addAccount')"
                    icon="pi pi-plus"
                    @click="confirmLinking"
                    :disabled="!isValid || !isValidEmail(accountMail)"
                />
                <ConfirmDialog group="confirm-linking">
                    <template #message="slotProps">
                        <p v-html="slotProps.message.message"></p>
                    </template>
                </ConfirmDialog>
            </div>
        </div>
        <small
            v-if="emailErrorMessage && emailErrorMessage !== ''"
            id="link-account-view_small_email-help"
            :style="{ color: 'red' }"
        >
            {{ emailErrorMessage }}
        </small>
        <Divider align="left">
            <b>{{ t("header.accountSettings.accountLinks.others.label") }}</b>
        </Divider>
        <TableSkeleton v-if="loading" :columns="columns" />
        <AccountLinkTable
            v-else
            direction="ACCESSED"
            :associated-accounts="accountsGrantedAccessByTrainer"
            :trainer="trainer"
            @update:links="updateLinks"
        />
        <Divider align="left">
            <b>{{ t("header.accountSettings.accountLinks.own.label") }}</b>
        </Divider>
        <TableSkeleton v-if="loading" :columns="columns" />
        <AccountLinkTable
            v-else
            direction="ACCESSIBLE"
            :associated-accounts="accountsAccessibleByTrainer"
            :trainer="trainer"
            @update:links="updateLinks"
            @update:impersonated="emit('update:impersonated')"
        />
        <template #footer>
            <div class="flex justify-end gap-2 mt-8">
                <Button
                    id="link-account-view_btn_close"
                    :label="t('buttons.close')"
                    @click="visible = !visible"
                    size="small"
                    autofocus
                />
            </div>
        </template>
    </Dialog>
</template>
